import type { Attributes } from "@opentelemetry/api";

/**
 * Survives a WebView process kill so the next launch can report `abnormal_exit`.
 * `sessionStorage` is wiped with the process, which is why the dirty record lives here.
 */
export const CRASH_SESSION_STORAGE_KEY = "novoweb.crashSession.v1";

/**
 * Set on `pagehide` so a same-tab refresh is not counted as a crash.
 * Process death clears `sessionStorage`, so a jetsam after `pagehide` still looks dirty.
 */
export const CRASH_SESSION_UNLOAD_ACK_KEY = "novoweb.crashSession.unloadAck";

type PersistableAttributes = Record<string, string | number | boolean>;

type CrashSessionRecord = {
    runtimeId: string;
    heartbeatMs: number;
    memory: PersistableAttributes;
    /** Scene / customer / GPU of the session that died — not the next launch. */
    identity?: PersistableAttributes;
};

const CRASH_SESSION_IDENTITY_KEYS = new Set(["scene.id", "company.id", "project.id"]);

/** Identifies this JS runtime so React Strict Mode remounts are not treated as crashes. */
let runtimeId: string | undefined;

function getLocalStorage(): Storage | undefined {
    try {
        return globalThis.localStorage;
    } catch {
        return undefined;
    }
}

function getSessionStorage(): Storage | undefined {
    try {
        return globalThis.sessionStorage;
    } catch {
        return undefined;
    }
}

function getRuntimeId(): string {
    if (!runtimeId) {
        runtimeId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    }
    return runtimeId;
}

function persistableAttributes(values: Attributes): PersistableAttributes {
    const out: PersistableAttributes = {};
    for (const [key, value] of Object.entries(values)) {
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            out[key] = value;
        }
    }
    return out;
}

/**
 * Identity Honeycomb Query 1 / 2 group by. Copied from telemetry globals at
 * heartbeat time so `abnormal_exit` is attributed to the session that died.
 */
function pickCrashSessionIdentity(
    globals: Readonly<Record<string, string | number | boolean>> | undefined,
): PersistableAttributes {
    const out: PersistableAttributes = {};
    if (!globals) {
        return out;
    }

    for (const [key, value] of Object.entries(globals)) {
        if (CRASH_SESSION_IDENTITY_KEYS.has(key) || key.startsWith("device.")) {
            out[key] = value;
        }
    }
    return out;
}

function readRecord(storage: Storage): CrashSessionRecord | undefined {
    const raw = storage.getItem(CRASH_SESSION_STORAGE_KEY);
    if (!raw) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(raw) as CrashSessionRecord;
        if (typeof parsed?.runtimeId !== "string") {
            return undefined;
        }
        return parsed;
    } catch {
        return undefined;
    }
}

/**
 * If the previous viewer session died without a clean shutdown, returns its last
 * memory snapshot and identity (scene / company / project / device.*) so the
 * caller can beacon `abnormal_exit` attributed to the session that died. Always
 * consumes the stored record so a crash is reported at most once.
 */
export function consumeUncleanCrashSession(): Record<string, string | number | boolean> | undefined {
    const local = getLocalStorage();
    const session = getSessionStorage();
    const unloadAck = session?.getItem(CRASH_SESSION_UNLOAD_ACK_KEY);

    if (!local) {
        return undefined;
    }

    const record = readRecord(local);

    if (record?.runtimeId === runtimeId) {
        return undefined;
    }

    local.removeItem(CRASH_SESSION_STORAGE_KEY);
    session?.removeItem(CRASH_SESSION_UNLOAD_ACK_KEY);

    if (!record || unloadAck) {
        return undefined;
    }

    return {
        ...record.memory,
        ...record.identity,
        "crash.session.heartbeat_ms": record.heartbeatMs,
    };
}

/** Persist (or refresh) the dirty marker so a process kill can be detected next launch. */
export function writeDirtyCrashSession(memory: Attributes, globals?: Attributes): void {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }

    // Do not clobber a previous JS runtime's record. `abnormal_exit` is deferred
    // until device globals are stamped, and the heartbeat runs on first paint.
    const existing = readRecord(storage);
    if (existing && existing.runtimeId !== getRuntimeId()) {
        return;
    }

    const identity = pickCrashSessionIdentity(persistableAttributes(globals ?? {}));
    const record: CrashSessionRecord = {
        runtimeId: getRuntimeId(),
        heartbeatMs: Date.now(),
        memory: persistableAttributes(memory),
        ...(Object.keys(identity).length > 0 ? { identity } : {}),
    };

    try {
        storage.setItem(CRASH_SESSION_STORAGE_KEY, JSON.stringify(record));
    } catch {
        // Quota or private-mode restrictions — crash detection degrades to in-process signals.
    }
}

/**
 * Host tore the viewer down on purpose (`Webviewer.terminate`). Next launch is not a crash.
 */
export function markCleanCrashShutdown(): void {
    runtimeId = undefined;
    try {
        getLocalStorage()?.removeItem(CRASH_SESSION_STORAGE_KEY);
        getSessionStorage()?.removeItem(CRASH_SESSION_UNLOAD_ACK_KEY);
    } catch {
        // Ignore storage access failures during teardown.
    }
}

/** Same-tab navigation / refresh: next load should not report `abnormal_exit`. */
export function ackControlledUnload(): void {
    try {
        getSessionStorage()?.setItem(CRASH_SESSION_UNLOAD_ACK_KEY, "1");
    } catch {
        // Ignore.
    }
}

/** Drop the in-memory runtime id as if the JS context was destroyed. Storage is left intact. */
export function clearCrashSessionRuntimeId(): void {
    runtimeId = undefined;
}
